import { config } from './config';

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const formatDate = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return timestamp;
  }
};

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat().format(num);
};

export const formatPercentage = (num: number): string => {
  return `${(num * 100).toFixed(1)}%`;
};

/**
 * Absolute URL for a seller logo.
 *
 * `/uploads` is mounted on the API (backend/main.py), not the frontend, and
 * there is no CRA dev proxy — so a bare `/uploads/logos/x.png` resolved against
 * the frontend origin and 404'd, even though the upload itself reported success.
 * `logo_path` is a server-side filesystem path, so only the basename is usable.
 */
export const sellerLogoUrl = (logoPath?: string | null): string | undefined => {
  if (!logoPath) return undefined;
  const filename = logoPath.split(/[\\/]/).pop();
  if (!filename) return undefined;
  const base = config.API_URL.replace(/\/$/, '');
  return `${base}/uploads/logos/${encodeURIComponent(filename)}`;
};

/**
 * Human label for a Stripe/backend subscription status.
 *
 * The raw values leaked into the UI, so users saw "trialing", "past_due" and
 * "canceled" — internal identifiers, one of them US-spelled, none of them
 * explaining what the user should do about it.
 */
const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Payment overdue',
  canceled: 'Cancelled',
  cancelled: 'Cancelled',
  unpaid: 'Unpaid',
  incomplete: 'Setup incomplete',
  incomplete_expired: 'Setup expired',
  paused: 'Paused',
  expired: 'Expired',
};

export const formatSubscriptionStatus = (status?: string | null): string => {
  if (!status) return 'Unknown';
  return (
    SUBSCRIPTION_STATUS_LABELS[status.toLowerCase()]
    // Unknown future statuses degrade to "Past due" rather than "past_due".
    ?? status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  );
};

/**
 * Format an amount with its currency, using the browser locale.
 *
 * Prefer this over hand-rolled symbol maps. A `CURRENCY_SYMBOLS` lookup that maps
 * both CAD and AUD to a bare `$` renders 1,000 CAD as "$1,000.00" — indistinguishable
 * from USD — and unmapped codes come out as "CHF1000". `Intl` disambiguates
 * (CA$1,000.00) and applies the right fraction digits per currency.
 *
 * An unknown or missing currency returns the bare number rather than inventing one.
 */
export const formatMoney = (amount: number, currency?: string | null): string => {
  const code = currency?.trim().toUpperCase();

  // Only ISO 4217 alpha-3 codes are valid for Intl; anything else (a bare symbol
  // like '€', or an empty string meaning "unknown") falls through to plain digits.
  if (code && /^[A-Z]{3}$/.test(code)) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(amount);
    } catch {
      /* Unrecognised code — fall through. */
    }
  }

  const number = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

  // A symbol we were handed directly (legacy uploaded-document data) still renders.
  return currency && !code?.match(/^[A-Z]{3}$/) ? `${currency}${number}` : number;
};